import { isEmpty } from 'lodash';
import WebLogRocket from 'logrocket';

import { Singleton } from '../../common/utils/Singleton';
import { LOGROCKET_WEB_APP_ID } from '../../config/config';
import { IS_LOCAL } from '../../config/FruitfulEnvironment';
import type { User } from '../../user/types/User';
import { getUserTraits, isJSON, sanitizeObject } from './LogRocketHelper';
import type { ILogRocket } from './types/LogRocket';

class LogRocketFacade extends Singleton implements ILogRocket {
    public readonly LogRocket: typeof WebLogRocket;

    constructor() {
        super();
        this.LogRocket = WebLogRocket;
    }

    public tagPage(pageName: string): void {
        if (!this.isEnabled()) {
            return;
        }

        this.LogRocket.track(`Page_${pageName}`);
    }

    public track(event: string, data?: object): void {
        if (!this.isEnabled()) {
            return;
        }

        // @ts-expect-error the TrackEventProperties type expected for 'data' is not exported by LR
        this.LogRocket.track(event, data);
    }

    public pauseViewCapture(): void {}

    public unpauseViewCapture(): void {}

    public log(message: string): void {
        if (!this.isEnabled()) {
            return;
        }

        this.LogRocket.log(message);
    }

    public captureWarning(message: string, exception?: Error): void {
        if (!this.isEnabled()) {
            return;
        }

        this.LogRocket.warn(['Warning:', message, exception ?? '']);
    }

    public captureException(
        exception: Error,
        data?: Record<string, unknown>,
    ): void {
        if (!this.isEnabled()) {
            return;
        }

        this.LogRocket.captureException(exception, data);
    }

    public getRedactProps(): object {
        return { 'data-private': true };
    }

    public identifyUser(user: User): void {
        if (!this.isEnabled()) {
            return;
        }

        this.LogRocket.identify(user.id, getUserTraits(user));
    }

    public identifyVisitor(visitorId: string): void {
        if (!this.isEnabled()) {
            return;
        }

        this.LogRocket.identify(visitorId, { isVisitor: true });
    }

    public isEnabled(): boolean {
        return !IS_LOCAL && LOGROCKET_WEB_APP_ID != null;
    }

    public init(): void {
        if (!this.isEnabled()) {
            return;
        }

        this.LogRocket.init(LOGROCKET_WEB_APP_ID as string, {
            dom: {
                inputSanitizer: true,
            },
            network: {
                responseSanitizer: (response) => {
                    if (
                        !isEmpty(response.body) &&
                        response.body != null &&
                        isJSON(response.body)
                    ) {
                        try {
                            const parsedBody = JSON.parse(
                                response.body,
                            ) as object;
                            const sanitizedBody = sanitizeObject(parsedBody);

                            return {
                                body: JSON.stringify(sanitizedBody),
                                statusCode: response.status,
                                status: response.status,
                                reqId: response.reqId,
                                headers: response.headers,
                                method: response.method,
                            };
                        } catch (e) {
                            this.LogRocket.error(e);
                            response.body = undefined;
                        }
                    }

                    return response;
                },
                requestSanitizer: (request) => {
                    if (
                        !isEmpty(request.body) &&
                        request.body != null &&
                        isJSON(request.body)
                    ) {
                        try {
                            const parsedBody = JSON.parse(
                                request.body,
                            ) as object;
                            const sanitizedBody = sanitizeObject(parsedBody);
                            request.body = JSON.stringify(sanitizedBody);
                        } catch (e) {
                            this.LogRocket.error(e);
                            request.body = undefined;
                        }
                    }

                    return request;
                },
            },
        });
    }
}

export default LogRocketFacade.getInstance();
